<template>
  <section v-if="title || description">
    <div
      class="mx-auto xl:px-0"
      v-if="imagePosition === 'right'"
      :class="[{ 'xl:max-w-6xl': !totalWidth }]"
    >
      <div
        class="lg:flex xl:gap-6 xl:space-y-0"
        :class="[centered, rounded, bgColor, { 'border border-gray-100 py-5 px-10': cardFormat }]"
      >
        <div
          :class="{ 'lg:w-1/2 ': imageSrc }"
          v-motion
          :initial="{ opacity: 0, y: 75 }"
          :visible-once="{
            opacity: 1,
            y: 0,
            scale: 1,
            transition: {
              delay: 200,
              duration: 800,
              opacity: {
                duration: 800
              }
            }
          }"
        >
          <h2
            :class="[
              titleSize,
              titleColor,
              titleWeight,
              { 'pt-4': centered },
              { 'text-white': darkBg },
              { 'text-center lg:text-start': imageSrc },
              { 'text-center': !imageSrc }
            ]"
            v-if="title"
          >
            {{ title }}
            <br v-if="secondLineTitle" />
            {{ secondLineTitle }}
            <br v-if="thirdLineTitle" />
            {{ thirdLineTitle }}
          </h2>
          <p
            class="mt-8 mx-2 sm:mx-16 lg:mx-0 text-center lg:text-start"
            :class="[{ 'text-white': darkBg, 'text-gray-600': !darkBg }, textSize, textColor]"
            v-if="description"
          >
            {{ description }}
            <span class="block" v-if="descriptionSecondText">
              <br />
              {{ descriptionSecondText }}
            </span>
          </p>
          <div class="mt-6 flex items-center flex-row justify-center lg:justify-start mb-4">
            <button
              v-if="buttonText && !shinyButton"
              @click="openLink(link)"
              type="submit"
              title="Get a demo"
              class="inline-flex items-center mr-2 sm:mr-4 h-10 px-3 border rounded-lg border-gray-400 hover:border-black transition-border-colors duration-300"
              :class="[
                { 'text-white': darkBg, 'bg-black': darkBg },
                { 'text-slate-900': !darkBg, 'bg-white': !darkBg }
              ]"
            >
              <span
                class="w-max font-semibold lg:block text-base"
                :class="[{ 'text-white': darkBg }, { 'text-slate-900': !darkBg }]"
              >
                {{ buttonText }}
              </span>
              <span class="shine-border"></span>
            </button>

            <button-shiny v-if="shinyButton" class="mr-5" :button-text="buttonText" />

            <button
              v-if="buttonSecondaryText"
              @click="openLink(link)"
              type="submit"
              title="Get a demo"
              class="inline-flex items-center h-10 px-3 border border-transparent rounded-lg"
              :class="[{ 'text-slate-900 bg-white': darkBg }, { 'text-white bg-black': !darkBg }]"
            >
              <span class="w-max font-semibold lg:block text-base">
                {{ buttonSecondaryText }}
              </span>
            </button>
          </div>
        </div>

        <div v-if="imageSrc" class="lg:pl-4" :class="{ 'lg:w-1/2': imageSrc }">
          <Image :src="imageSrc" alt="image" :class="imageRounded" class="mx-auto" />
        </div>
      </div>
    </div>

    <div
      class="mx-auto xl:px-0"
      v-if="imagePosition === 'left'"
      :class="[{ 'xl:max-w-6xl': !totalWidth }]"
    >
      <div
        class="lg:flex xl:gap-6 xl:space-y-0"
        :class="[centered, bgColor, { 'rounded-xl border border-gray-100 p-5': cardFormat }]"
      >
        <div
          class="items-center order-2 pl-lg:pl-10"
          :class="{ 'lg:w-1/2 ': imageSrc }"
          v-motion
          :initial="{ opacity: 0, y: 100 }"
          :visible-once="{
            opacity: 1,
            y: 0,
            scale: 1,
            transition: {
              delay: 200,
              duration: 800,
              opacity: {
                duration: 800
              }
            }
          }"
        >
          <div class="lg:flex lg:flex-col lg:items-start">
            <h2
              class="text-center lg:text-start text-2xl"
              :class="[
                titleColor,
                titleSize,
                titleWeight,
                { 'pt-4': centered },
                { 'text-white': darkBg }
              ]"
              v-if="title"
            >
              {{ title }}
              <br v-if="secondLineTitle" />
              {{ secondLineTitle }}
              <br v-if="thirdLineTitle" />
              {{ thirdLineTitle }}
            </h2>
            <p
              class="mt-8 text-center lg:text-start"
              :class="[{ 'text-white': darkBg }, textSize, textColor]"
              v-if="description"
            >
              {{ description }}
              <span class="block" v-if="descriptionSecondText">
                <br />
                {{ descriptionSecondText }}
              </span>
            </p>
            <div class="mt-6 flex flex-row justify-center lg:justify-start mb-4">
              <button
                v-if="buttonText"
                @click="openLink(link)"
                type="submit"
                title="Get a demo"
                class="inline-flex items-center mr-2 sm:mr-4 lg:mb-0 h-10 px-3 sm:mt-0 border rounded-lg border-gray-400 hover:border-black transition-colors"
                :class="[{ 'text-white bg-black': darkBg }, { 'text-slate-900 bg-white': !darkBg }]"
              >
                <span
                  class="w-max font-semibold lg:block text-base"
                  :class="[{ 'text-white': darkBg }, { 'text-slate-900': !darkBg }]"
                >
                  {{ buttonText }}
                </span>
              </button>
              <button
                v-if="buttonSecondaryText"
                @click="openLink(link)"
                type="submit"
                title="Get a demo"
                class="inline-flex items-center h-10 px-3 border border-transparent rounded-lg"
                :class="[{ 'text-slate-900 bg-white': darkBg }, { 'text-white bg-black': !darkBg }]"
              >
                <span class="w-max font-semibold lg:block text-base">
                  {{ buttonSecondaryText }}
                </span>
              </button>
            </div>
          </div>
        </div>

        <div
          v-if="imageSrc"
          class="order-1 items-center mt-6 sm:mt-8 lg:pr-4"
          :class="{ 'lg:w-1/2': imageSrc }"
        >
          <Image :src="imageSrc" alt="image" class="mx-auto" :class="imageRounded" />
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import router from '@/router';
import { Image } from '@unpic/vue';
import ButtonShiny from '@/components/buttonShiny.vue';

defineProps({
  title: String,
  secondLineTitle: String,
  thirdLineTitle: String,
  totalWidth: {
    type: Boolean,
    default: false
  },
  link: {
    type: String,
    default: ''
  },
  darkBg: {
    type: Boolean,
    default: false
  },
  shinyButton: {
    type: Boolean,
    default: false
  },
  titleColor: {
    type: String,
    default: 'text-gray-800'
  },
  titleSize: {
    type: String,
    default: 'text-4xl'
  },
  rounded: {
    type: String,
    default: 'rounded-none'
  },
  textColor: {
    type: String,
    default: 'text-gray-600'
  },
  textSize: {
    type: String,
    default: 'text-base'
  },
  titleWeight: {
    type: String,
    default: 'font-normal'
  },
  centered: {
    type: String,
    default: 'items-center'
  },
  bgColor: {
    type: String,
    default: 'bg-transparent'
  },
  cardFormat: {
    type: Boolean,
    default: false
  },
  description: String,
  descriptionSecondText: String,
  buttonText: String,
  buttonSecondaryText: String,
  imageSrc: String,
  imageRounded: {
    type: String,
    default: 'rounded-none'
  },
  imagePosition: {
    type: String,
    default: 'right'
  }
});

const openLink = (link) => {
  router.push(link);
};
</script>
